import { useUser } from '@lore/auth';
import React, {useEffect, useRef, useState} from 'react';
import { Helmet } from 'react-helmet-async';
import Partners from './Partners';
import useRouter from '@/hooks/useRouter';
import PageHeading from '@/@communityshare/ui/PageHeading';
import Results from '@/pages/_common_results/Results';
import User from '@/pages/_common_user/ManageUser';
import DateJoined from '@/pages/_common_user/User/DateJoined';
import LastActive from '@/pages/_common_user/User/LastActive';
import Filters from '@/forms/filters_partners';
import { UsersIcon } from '@heroicons/react/24/outline';
import MobileNavigation from './MobileNavigation';
import Redirect from '@/components/Redirect';
import {t} from "i18next";
import useUserNotificationStates from "@/hooks/useUserNotificationStates";
import Loader from "@/components/Loader";
import moment from "moment";
import { Alert } from '@communityshare/ui-shared';

export default function Layout() {
  const router = useRouter();
  const currentUser = useUser();
  const showAll = !currentUser.lowerNetworkBarriers
  const hasUser = !!currentUser?.data?.id;

  const {
    statesByKey,
    isLoading,
    isFetching,
    isError,
    isSuccess,
    markShown,
    snooze,
    dismiss
  } = useUserNotificationStates({ enabled: hasUser });

  const [showAlert, setShowAlert] = useState(false);
  const markedAlert = useRef(false);
  useEffect(() => {
    if (markedAlert.current) return
    if (isLoading || isFetching) return
    markedAlert.current = true

    if (isSuccess && 'alert:steward-all-partners-update' in statesByKey) {
      const alert = statesByKey['alert:steward-all-partners-update'];
      if (alert?.status === "queued") {
        setTimeout(() => {
          setShowAlert(true);
          markShown('alert:steward-all-partners-update', 'alert');
        }, 2000);
      }
      else if (alert?.status === "snoozed") {
        const sevenDaysAgo = moment().subtract(7, 'days');
        const isMoreThanSevenDaysAgo = moment(alert.shownAt).isBefore(sevenDaysAgo);
        if (isMoreThanSevenDaysAgo) {
          setTimeout(() => {
            setShowAlert(true);
            markShown('alert:steward-all-partners-update', 'alert');
          }, 2000);
        }
      }
      // all other status "dismissed", "not_applicable", etc we do not show the alert
    }
    // if we have trouble with the alert loading, just show it
    else setShowAlert(true);

  }, [statesByKey, isLoading, isFetching, isError, isSuccess]);

  function handleSnooze() {
    snooze('alert:steward-all-partners-update', 'alert');
    setShowAlert(false);
  }

  function handleDismiss() {
    dismiss('alert:steward-all-partners-update', 'alert');
    setShowAlert(false);
  }

  if (!showAll) {
    return (<Redirect to="/partners/related" />)
  }

  if (isLoading || isFetching)
    return <Loader/>

  return (
    <div className="p-8 space-y-8">
      <Helmet>
        <title>{t('partners.explore_title')}</title>
      </Helmet>
      <MobileNavigation />
      <PageHeading
        breadcrumbs={[t('partners.breadcrumb_explore'), t('partners.breadcrumb_partners')]}
        title={t('partners.all')}
        description={(
          <>
            {t('partners.all_subtitle')}
          </>
        )}
      />
      <Alert
        title={t('partners.all_alert_title')}
        subtitle={t('partners.all_alert_subtitle')}
        mode="info"
        onDismissed={handleSnooze}
        actionLabel={t('partners.all_alert_action')}
        onAction={handleDismiss}
       show={showAlert}/>
      <Filters router={router} />
      <Partners router={router}>
        {function(result) {
          return (
            <Results result={result} columns={3} resource="partners" emptyIcon={UsersIcon}>
              {function(user, index) {
                return (
                  <User
                    key={index}
                    user={user}
                  >
                    <div className="bg-cs-gray-50 p-4 border-t border-cs-gray-100">
                      <div className="grid grid-cols-2">
                        <LastActive user={user} />
                        <DateJoined user={user} align="right" />
                      </div>
                    </div>
                  </User>
                );
              }}
            </Results>
          );
        }}
      </Partners>
    </div>
  );
}
